import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  styled
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import { getAllGames } from '../apiCalls/games/api_v1_games';

import CallofDuty from '../static/img/news/img_callofduty.avif';
import Fortnite from '../static/img/news/img_fortnite.avif';
import Valorant from '../static/img/news/img_valorant.avif';
import Freefire from '../static/img/news/img_freefire.avif';
import Minecraft from '../static/img/news/img_minecraft.avif';
import Pubg from '../static/img/news/img_pubg.avif';

const NewsCard = styled(Box)(({ theme, isFirst }) => ({
  position: 'relative',
  width: '100%',
  height: '200px',
  borderRadius: '12px',
  overflow: 'hidden',
  cursor: 'pointer',
  backgroundColor: '#2A2A2A',
  border: '2px solid #2A2A2A',
  borderTop: '2px solid #FAAE1C',
  '&:hover': {
    transform: 'scale(1.02)',
    transition: 'transform 0.2s ease-in-out',
    backgroundColor: '#FAAE1C',
    border: '2px solid #FAAE1C',
  },
}));

const GameImage = styled('div')(({ theme }) => ({
  width: '100%',
  height: '150px',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
}));

const GameNameSection = styled(Box)(({ theme, isFirst }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: '40px',
  backgroundColor: 'inherit',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2,
}));

const GameTitle = styled(Typography)(({ theme, isFirst }) => ({
  fontFamily: "'Poppins', sans-serif",
  fontSize: '16px',
  fontWeight: '600',
  color: '#FFFFFF',
  textAlign: 'center',
  '.NewsCard:hover &': {
    color: '#000000',
  },
}));

function News() {
  const theme = useTheme();
  const isDesktop = useMediaQuery('(min-width:900px)');
  const navigate = useNavigate();
  
  const [games, setGames] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  
  const getGameImage = (gameName) => {
    switch (gameName) {
      case 'Fortnite':
        return Fortnite;
      case 'PUBG':
        return Pubg;
      case 'Call of Duty':
        return CallofDuty;
      case 'Minecraft':
        return Minecraft;
      case 'Valorant':
        return Valorant;
      case 'Freefire':
        return Freefire;
      default:
        return CallofDuty;
    }
  };
  
  useEffect(() => {
    const fetchGames = async () => {
      try {
        const response = await getAllGames();
        if (response.data.success) {
          const gamesData = response.data.data.games.map(game => ({
            id: game.uuid,
            name: game.name,
            thumbnail: getGameImage(game.name),
            uuid: game.uuid
          }));
          setGames(gamesData);
        }
      } catch (error) {
        console.error('Error fetching games:', error);
        setGames([
          { id: 1, name: 'Call of Duty', thumbnail: CallofDuty },
          { id: 2, name: 'Valorant', thumbnail: Valorant },
          { id: 3, name: 'PUBG', thumbnail: Pubg },
          { id: 4, name: 'Fortnite', thumbnail: Fortnite },
          { id: 5, name: 'Freefire', thumbnail: Freefire },
          { id: 6, name: 'Minecraft', thumbnail: Minecraft }
        ]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchGames();
  }, []);
  
  const filteredGames = games.filter(game =>
    game.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    document.title = "News & Articles | Gamez Academy";
  }, []);

  const handleGameClick = (gameName) => {
    const formattedGameName = gameName.toLowerCase().replace(/\s+/g, '-');
    navigate(`/news/${formattedGameName}`);
  };

  return (
    <Box
      sx={{
        width: '100%',
        padding: isDesktop ? '20px' : '16px',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
        backgroundColor: theme.palette.background.default,
        minHeight: '100vh'
      }}
    >
      <Typography
        sx={{
          fontFamily: "'Poppins', sans-serif",
          fontSize: '16px',
          fontWeight: '500',
          lineHeight: '133%',
          textAlign: 'left',
        }}
      >
        Latest News, Article, Reviews, Pros & Cons
      </Typography>
      <Typography
        sx={{
          fontFamily: "'Poppins', sans-serif",
          fontSize: '12px',
          fontWeight: '300',
          color: '#F7F7F7',
          textAlign: 'left',
          marginBottom: '16px'
        }}
      >
        Read Everyday news, Articles & reviews to stay update.
      </Typography>
      <TextField
        placeholder="Search your favourite game"
        variant="outlined"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{
          width: '100%',
          marginBottom: '24px',
          '& .MuiOutlinedInput-root': {
            fontFamily: "'Poppins', sans-serif",
            fontSize: '14px',
            lineHeight: '24px',
            backgroundColor: '#2A2A2A',
            borderRadius: '8px',
            '& fieldset': {
              borderColor: '#444444',
            },
            '&:hover fieldset': {
              borderColor: '#FAAE1C',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#FAAE1C',
            },
          },
          '& .MuiOutlinedInput-input': {
            color: '#FFFFFF',
            '&::placeholder': {
              color: '#D9D9D9',
              opacity: 1,
            },
          },
          '& .MuiInputAdornment-root .MuiSvgIcon-root': {
            color: '#D9D9D9',
            transition: 'color 0.2s ease',
          },
          '&:hover .MuiInputAdornment-root .MuiSvgIcon-root': {
            color: '#FFFFFF',
          },
          '&.Mui-focused .MuiInputAdornment-root .MuiSvgIcon-root': {
            color: '#FFFFFF',
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isDesktop ? 'repeat(auto-fit, minmax(300px, 1fr))' : '1fr',
          gap: '16px',
          gridAutoRows: 'max-content'
        }}
      >
      {loading ? (
        Array.from({ length: 6 }).map((_, index) => (
          <NewsCard key={`loading-${index}`} isFirst={index === 0}>
            <GameImage
              sx={{
                backgroundImage: `url(${CallofDuty})`,
                margin: '6px',
                width: 'calc(100% - 12px)',
                borderRadius: '8px',
                opacity: 0.3
              }}
            />
            <GameNameSection isFirst={index === 0}>
              <GameTitle isFirst={index === 0} className="game-title">
                Loading...
              </GameTitle>
            </GameNameSection>
          </NewsCard>
        ))
      ) : (
        filteredGames.map((game, index) => (
          <NewsCard
            key={game.id}
            isFirst={index === 0}
            onClick={() => handleGameClick(game.name)}
            sx={{ 
              marginBottom: 0,
              '&:hover .game-title': {
                color: '#000000',
              }
            }}
          >
            <GameImage
              sx={{
                backgroundImage: `url(${game.thumbnail})`,
                margin: '6px',
                width: 'calc(100% - 12px)',
                borderRadius: '8px'
              }}
            />
            <GameNameSection isFirst={index === 0}>
              <GameTitle isFirst={index === 0} className="game-title">
                {game.name}
              </GameTitle>
            </GameNameSection>
          </NewsCard>
        ))
      )}
      </Box>
    </Box>
  );
}

export default News;